import { Component, forwardRef, input, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, } from '@angular/forms';
import { FilePondComponent, FilePondModule } from 'ngx-filepond';
import { FilePondBaseProps, FilePondInitialFile, FilePondOptions } from 'filepond';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@core/services/toast.service';
import { moveItem } from '@core/utils/array-utils';
import { File as FileModel } from '@core/models/file.model';
import Cropper from 'cropperjs';

@Component({
    selector: 'shared-form-control-file',
    imports: [
        FilePondModule,
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FormControlFile),
            multi: true,
        },
    ],
    template: `
        <div class="filepond-uploader three-grid">
            <file-pond
                #filePond
                [files]="defaultFiles"
                [options]="options()"
                (onaddfile)="onFileAdd($event)"
                (onreorderfiles)="onFileReorder($event)"
                (onremovefile)="onFileRemove($event)"
                (onerror)="onError($event)"
                (onwarning)="onError($event)"
            />
        </div>
    `,
})
export class FormControlFile implements ControlValueAccessor {

    @ViewChild('filePond') filePond!: FilePondComponent;


    accept = input<string[]>([]);
    allowReorder = input<boolean>(true);
    multiple = input<boolean>(false);
    maxFiles = input<number|undefined>(undefined);

    defaultFiles: FilePondInitialFile[] = [];
    isDisabled: boolean = false;

    constructor(
        private toastService: ToastService,
        private translateService: TranslateService,
    ) {

    }

    options = () => ({
        // Default
        labelIdle: this.translateService.instant('modules.filepond.drag_drop_or_click_to_upload'),
        allowMultiple: this.multiple(),
        allowReorder: this.allowReorder(),
        maxFiles: this.maxFiles() ?? null,
        disabled: this.isDisabled,
        className: 'filepond-input-multiple',
        itemInsertLocation: 'after',
        server: null,

        // Size
        allowFileSizeValidation: true,
        maxFileSize: '10MB',
        labelMaxFileSizeExceeded: this.translateService.instant('modules.filepond.size_validation.label_max_file_size_exceeded'),
        labelMaxFileSize: this.translateService.instant('modules.filepond.size_validation.label_max_file_size'),

        // Type
        allowFileTypeValidation: this.accept().length > 0,
        acceptedFileTypes: this.accept(),
        labelFileTypeNotAllowed: this.translateService.instant('modules.filepond.type_validation.label_file_type_not_allowed'),
        fileValidateTypeLabelExpectedTypes: this.translateService.instant('modules.filepond.type_validation.label_file_type_expected_types'),
    });

    onChange: (_: any) => void = () => {
    };
    onTouched: () => void = () => {
    };

    writeValue(value: any) {
        const files = [];

        if (Array.isArray(value)) {
            for (const file of value) {
                files.push(this.convertFileToFilePond(file));
            }
        } else if (value) {
            files.push(this.convertFileToFilePond(value));
        }

        this.defaultFiles = files;
    }

    convertFileToFilePond(file: FileModel): FilePondInitialFile {
        return {
            source: file.url,
            options: {
                type: 'local',
                file: {
                    name: file.original_name,
                    size: file.size,
                    type: file.mime_type,
                },
                metadata: {
                    id: file.id,
                    poster: file.url,
                }
            }
        };
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.isDisabled = isDisabled;
    }

    data: any[] = [];


    onError(event: ErrorEvent) {
        if (event.error.body) {
            this.toastService.error(event.error.body.message);
        }
    }

    onFileAdd(event: any) {
        if (event?.error === null) {
            if (event?.file?.file instanceof File) {
                this.data.push(event.file.file);
            } else if (event.file.getMetadata('id')) {
                this.data.push(event.file.getMetadata('id'));
            }
        }

        this.dataChange();
    }

    onFileRemove(event: any) {
        let value = null;

        if (event?.file?.file instanceof File) {
            value = event.file.file;
        } else if (event.file.getMetadata('id')) {
            value = event.file.getMetadata('id');
        }

        this.data = this.data.filter(f => f !== value);

        this.dataChange();
    }

    onFileReorder(event: any) {
        this.data = moveItem(this.data, event.origin, event.target);

        this.dataChange();
    }

    dataChange() {
        this.onChange(this.data);
    }
}
